import React, { FC, Fragment } from 'react';
import { pick } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import { Divider, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { compose } from 'recompose';

import { useUpdateUserProfileMutation, UserProfileInput } from 'api';
import { Layouts } from 'components';
import { withClientLocations, withClientDepartments } from 'providers/queries';
import { useCurrentUser, useFeatures, useNotify, useClient } from 'hooks';

import { AccountSchema } from '../validations';
import {
  Access,
  AccessSSO,
  Connections,
  Delete,
  Details,
  Integrations,
  Notifications,
  ProfileImage,
  Submit,
} from '../components';

const useStyles = makeStyles(theme => ({
  '@global body': {
    backgroundColor: theme.palette.common.white,
  },
  root: {
    marginBottom: theme.spacing(4),
  },
  dividerFirst: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(4),
  },
  divider: {
    marginTop: theme.spacing(8),
    marginBottom: theme.spacing(4),
  },
}));

const enhancers = compose(withClientLocations, withClientDepartments);

const EditAccount: FC = () => {
  const { t } = useTranslation();
  const { msteams, slack } = useFeatures();
  const classes = useStyles();
  const notify = useNotify();
  const currentUser = useCurrentUser();
  const { authentication_type } = useClient();
  const hasIntegrations = slack || msteams;

  const [updateUserProfile] = useUpdateUserProfileMutation({
    onCompleted: () => notify.info(t('components:savedSuccessfully')),
    onError: notify.mutationError,
  });

  const initialValues = pick(currentUser, [
    'id',
    'full_name',
    'job_title',
    'email_address',
    'location_id',
    'department_id',
    'image',
    'notification_delivery_types',
  ]);

  function handleSubmit(values: UserProfileInput) {
    const { department_id, location_id, image, ...rest } = values;
    const data: UserProfileInput = {
      image,
      department_id: department_id ? department_id : null,
      location_id: location_id ? location_id : null,
      ...rest,
    };

    if (image && !image.localUri) {
      delete data.image;
    }

    return updateUserProfile({ variables: { data } });
  }

  function renderMain() {
    return (
      <div className={classes.root}>
        <Typography color="inherit" variant="h4">
          {t('user:accountSettings')}
        </Typography>
        <Divider className={classes.dividerFirst} />
        <Details />
        <Divider className={classes.divider} />
        {authentication_type !== 'auth' ? <AccessSSO /> : <Access />}
        <Divider className={classes.divider} />
        <ProfileImage />
        <Divider className={classes.divider} />
        <Connections />
        {hasIntegrations && (
          <Fragment>
            <Divider className={classes.divider} />
            <Integrations />
          </Fragment>
        )}
        <Divider className={classes.divider} />
        <Notifications />
        <Divider className={classes.divider} />
        <Grid container justify="space-between">
          <Grid item>
            <Delete />
          </Grid>
          <Grid item>
            <Submit />
          </Grid>
        </Grid>
      </div>
    );
  }

  return (
    <Formik onSubmit={handleSubmit} initialValues={initialValues} validationSchema={AccountSchema}>
      <Form>
        <Layouts.App1Column
          desktopSize={11}
          main={<Layouts.AppMain1Column main={renderMain()} />}
        />
      </Form>
    </Formik>
  );
};

export default enhancers(EditAccount);
